import type { TConversationThreadMessageDTO } from '@conversed/rest/src/contracts/train-ai/train-ai.dto';

type TThreadTopic = NonNullable<TConversationThreadMessageDTO['hotTopic']>;

/**
 * The topics the conversation touched, each listed once, in the order they first came up.
 */
export const getDistinctThreadTopics = (
  messages: TConversationThreadMessageDTO[],
): TThreadTopic[] => {
  const topicsById = new Map<string, TThreadTopic>();

  for (const message of messages) {
    if (message.hotTopic && !topicsById.has(message.hotTopic.id)) {
      topicsById.set(message.hotTopic.id, message.hotTopic);
    }
  }

  return [...topicsById.values()];
};

type TFittingChipsInput = {
  chipWidths: number[];
  availableWidth: number;
  // Width of the "+N more" button that has to fit after the chips when some are hidden.
  moreButtonWidth: number;
  gap: number;
};

/**
 * How many chips fit on one line. When they do not all fit, room is kept for the
 * "+N more" button. At least one chip is always shown - it truncates if it is too wide.
 */
export const countFittingChips = ({
  chipWidths,
  availableWidth,
  moreButtonWidth,
  gap,
}: TFittingChipsInput): number => {
  const totalWidth =
    chipWidths.reduce((sum, chipWidth) => sum + chipWidth, 0) +
    gap * Math.max(0, chipWidths.length - 1);

  if (totalWidth <= availableWidth) {
    return chipWidths.length;
  }

  const widthForChips = availableWidth - moreButtonWidth - gap;
  let usedWidth = 0;
  let fittingCount = 0;

  for (const chipWidth of chipWidths) {
    const nextUsedWidth = usedWidth + (fittingCount > 0 ? gap : 0) + chipWidth;

    if (nextUsedWidth > widthForChips) {
      break;
    }

    usedWidth = nextUsedWidth;
    fittingCount += 1;
  }

  return Math.max(1, fittingCount);
};
